import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { invalidateInboxStatusCache, type InboxProviderId } from '@/services/inboxSync';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import { clearInboxOauthMarkers, getAllowedOauthOrigins, isTrustedOauthOrigin, setInboxOauthSuccess } from '@/utils/inboxOauth';
import { pollInboxStatusUntilConnected } from '@/utils/inboxOauthResume';
import { logInboxSyncDebug } from '@/utils/inboxSyncDebug';

type InboxStatusResult = { connected?: boolean } | null | undefined;

type UseInboxOauthPostMessageOptions = {
  userId?: string;
  enabled?: boolean;
  projectId?: string;
  checkStatus: (provider: InboxProviderId) => Promise<InboxStatusResult>;
  onConnected: (provider: InboxProviderId) => Promise<void> | void;
  canResume?: () => boolean;
};

const getProviderFromOauthEvent = (eventData: any): InboxProviderId | null => {
  if (eventData?.type === 'gmail-oauth-success') return 'gmail';
  if (eventData?.type === 'outlook-oauth-success') return 'outlook';
  return null;
};

export function useInboxOauthPostMessage({
  userId,
  enabled = true,
  projectId,
  checkStatus,
  onConnected,
  canResume,
}: UseInboxOauthPostMessageOptions): void {
  const runningRef = useRef(false);

  const run = useCallback(
    async (provider: InboxProviderId) => {
      if (!userId || !enabled) return;
      if (canResume && !canResume()) return;
      if (runningRef.current) return;

      logInboxSyncDebug('postMessage.run.started', { userId, provider, enabled });
      runningRef.current = true;
      try {
        await setInboxOauthSuccess(provider).catch(() => {});
        logInboxSyncDebug('postMessage.run.success_marker_set', { provider });

        const connected = await pollInboxStatusUntilConnected(provider, checkStatus, {
          baseDelayMs: 600,
        });
        if (!connected) {
          logInboxSyncDebug('postMessage.run.not_connected_after_poll', { provider });
          return;
        }

        invalidateInboxStatusCache();
        await useInboxConnectionsStore.getState().refreshFresh();
        await clearInboxOauthMarkers();
        logInboxSyncDebug('postMessage.run.connected', { provider });
        await onConnected(provider);
        logInboxSyncDebug('postMessage.run.on_connected_complete', { provider });
      } finally {
        runningRef.current = false;
        logInboxSyncDebug('postMessage.run.finished');
      }
    },
    [userId, enabled, canResume, checkStatus, onConnected],
  );

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (!userId || !enabled) return;
    if (canResume && !canResume()) return;

    const handler = async (event: MessageEvent) => {
      const allowedOrigins = getAllowedOauthOrigins(projectId);
      if (!isTrustedOauthOrigin(event.origin, allowedOrigins)) {
        logInboxSyncDebug('postMessage.event.ignored_origin', { origin: event.origin });
        return;
      }

      const provider = getProviderFromOauthEvent(event.data);
      if (!provider) {
        logInboxSyncDebug('postMessage.event.ignored_type', {
          type: event?.data?.type,
        });
        return;
      }
      logInboxSyncDebug('postMessage.event.accepted', { provider, origin: event.origin });
      await run(provider);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [userId, enabled, projectId, canResume, run]);
}
